import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  Download, FileSpreadsheet, Calendar, DollarSign, Clock,
  Users, Loader2, TrendingUp, Banknote, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '@/lib/utils';

import { API } from '@/config/api';

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

// Stat Card
function StatCard({ title, value, icon: Icon, subtitle, color = "blue" }) {
  const colorClasses = {
    blue: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    green: "bg-green-500/10 text-green-600 dark:text-green-400",
    purple: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
    orange: "bg-orange-500/10 text-orange-600 dark:text-orange-400"
  };

  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold text-foreground mt-1">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <div className={cn("h-12 w-12 rounded-xl flex items-center justify-center", colorClasses[color])}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PayrollExportPage() {
  const { token } = useOutletContext();
  
  // State
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 13); // Last 2 weeks
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [exportFormat, setExportFormat] = useState('csv');
  const [payrollData, setPayrollData] = useState(null);
  const [summary, setSummary] = useState(null);
  
  // Fetch payroll preview
  const fetchPayrollPreview = useLatestCallback(async () => {
    setLoading(true);
    try {
      // Get JSON preview
      const res = await axios.get(`${API}/labor/payroll/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { from: fromDate, to: toDate, format: 'json' }
      });
      
      setPayrollData(res.data.employees || []);
      setSummary(res.data.summary || {});
    } catch (error) {
      console.error('Error fetching payroll:', error);
      toast.error('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  });
  
  // Initial fetch
  useEffect(() => {
    if (token) {
      fetchPayrollPreview();
    }
  }, [token, fetchPayrollPreview]);
  
  // Download export
  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/labor/payroll/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { from: fromDate, to: toDate, format: exportFormat },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const formatSuffix = exportFormat === 'adp' ? 'adp_import' : 
                          exportFormat === 'gusto' ? 'gusto_import' : 
                          'payroll';
      a.download = `${formatSuffix}_${fromDate}_to_${toDate}.csv`;
      a.click();
      
      toast.success(`${exportFormat.toUpperCase()} export downloaded!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to download export');
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-export-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Payroll Export</h1>
          <p className="text-muted-foreground mt-1">Export hours and tips for payroll processing</p>
        </div>
      </div>

      {/* Date Range & Format Selection */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                className="w-40"
              />
            </div>
            
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                className="w-40"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Export Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">Standard CSV</SelectItem>
                  <SelectItem value="adp">ADP Import</SelectItem>
                  <SelectItem value="gusto">Gusto Import</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button onClick={fetchPayrollPreview} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Calendar className="h-4 w-4 mr-2" />
              )}
              Load Period
            </Button>
            
            <Button onClick={handleExport} disabled={!payrollData?.length}>
              <Download className="h-4 w-4 mr-2" />
              Download {exportFormat.toUpperCase()}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Employees"
            value={summary.total_employees || 0}
            icon={Users}
            color="blue"
          />
          <StatCard
            title="Total Hours"
            value={`${summary.total_hours?.toFixed(1) || 0}h`}
            icon={Clock}
            color="green"
          />
          <StatCard
            title="Total Tips"
            value={formatCurrency(summary.total_tips)}
            icon={Banknote}
            color="purple"
          />
          <StatCard
            title="Gross Pay"
            value={formatCurrency(summary.total_gross_pay)}
            icon={TrendingUp}
            color="orange"
          />
        </div>
      )}

      {/* Payroll Preview Table */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground">Payroll Preview</CardTitle>
          <CardDescription>
            Review data before exporting. Period: {fromDate} to {toDate}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
            </div>
          ) : payrollData?.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Regular</TableHead>
                    <TableHead className="text-right">Overtime</TableHead>
                    <TableHead className="text-right">Reg Pay</TableHead>
                    <TableHead className="text-right">OT Pay</TableHead>
                    <TableHead className="text-right">Tips</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payrollData.map(emp => (
                    <TableRow key={emp.employee_id}>
                      <TableCell>
                        <div>
                          <div className="font-medium text-foreground">{emp.employee_name}</div>
                          <div className="text-xs text-muted-foreground">{emp.email}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(emp.hourly_rate)}/hr
                      </TableCell>
                      <TableCell className="text-right">
                        {emp.regular_hours.toFixed(1)}h
                      </TableCell>
                      <TableCell className="text-right">
                        {emp.overtime_hours > 0 ? (
                          <span className="text-orange-600 dark:text-orange-400">
                            {emp.overtime_hours.toFixed(1)}h
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(emp.regular_pay)}
                      </TableCell>
                      <TableCell className="text-right">
                        {emp.overtime_pay > 0 ? formatCurrency(emp.overtime_pay) : '-'}
                      </TableCell>
                      <TableCell className="text-right text-green-600 dark:text-green-400">
                        {emp.tips > 0 ? formatCurrency(emp.tips) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(emp.gross_pay)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FileSpreadsheet className="h-12 w-12 mx-auto opacity-30" />
              <p className="mt-4">No payroll data for this period</p>
              <p className="text-sm mt-2">
                Employees need time entries and finalized tip pools to appear here
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Format Info */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground text-base">Export Formats</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-blue-500" />
                <h4 className="font-medium">Standard CSV</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Universal format with all payroll fields. Import into any payroll system.
              </p>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet className="h-5 w-5 text-green-500" />
                <h4 className="font-medium">ADP Import</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Formatted for ADP Workforce Now bulk import. Includes company code and batch ID.
              </p>
            </div>
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet className="h-5 w-5 text-purple-500" />
                <h4 className="font-medium">Gusto Import</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                Formatted for Gusto payroll import. Uses email as employee identifier.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
